<template>
	<label
		class="checkbox"
		ref="label"
		:disabled="disabled"
		@click="focus"
		@keydown.prevent.enter="$refs.label.click()"
	>
		<input
			v-model="computedValue"
			type="checkbox"
			ref="input"
			@click.stop
			:disabled="disabled"
			:required="required"
			:name="name"
			:value="nativeValue"
			:true-value="trueValue"
			:false-value="falseValue"
		/>
		<span role="checkbox" class="custom-checkbox"></span>
		<span class="control-label"><slot /></span>
	</label>
</template>

<script>
/*
 * This component is largely derived from the Checkbox component in the Buefy framework v0.9.4.
 * It supports two-way databinding via v-model in much the same way as the default Vue inputs do.
 * For more information see: https://buefy.org/documentation/checkbox
 * Props:
 * value - Like most inputs, you'll typically you'll want to use v-model rather than use value prop.
 * nativeValue - Utilized when you have a grouped of checkboxes all with the same Array v-model.
 * trueValue, falseValue, - These already exist on the default Vue checkboxes.  It allows you to have
 * alternative values instead of true of false. See the Vue documentation for true-value and false-value
 * for more information.
 */
export default {
	name: 'FormCheckbox',
	props: {
		value: [String, Number, Boolean, Function, Object, Array],
		nativeValue: [String, Number, Boolean, Function, Object, Array],
		disabled: Boolean,
		required: Boolean,
		name: String,
		trueValue: {
			type: [String, Number, Boolean, Function, Object, Array],
			default: true,
		},
		falseValue: {
			type: [String, Number, Boolean, Function, Object, Array],
			default: false,
		},
	},
	data() {
		return {
			newValue: this.value,
		};
	},
	computed: {
		computedValue: {
			get() {
				return this.newValue;
			},
			set(value) {
				this.newValue = value;
				this.$emit('input', value);
			},
		},
	},
	watch: {
		/**
		 * When v-model (value) changes, set internal value.
		 */
		value(value) {
			this.newValue = value;
		},
	},
	methods: {
		focus() {
			// MacOS FireFox and Safari do not focus when clicked
			this.$refs.input.focus();
		},
	},
};
</script>

<style lang="scss" scoped>
.checkbox {
	display: inline-flex;
	position: relative;
	@include rem('margin-bottom', 12px);
	cursor: pointer;

	/* On mouse-over, add a blue border color to custom-checkbox */
	&:hover {
		.custom-checkbox {
			border-color: $brand-blue;
		}
	}

	/* Create a custom checkbox */
	.custom-checkbox {
		flex-shrink: 0;
		position: relative;
		@include rem('width', 16px);
		@include rem('height', 16px);
		background: $white;
		@include rem('border-radius', 5px);
		border: 1px solid $border-color-light;

		/* Create the checkmark and hide it when not checked */
		&:after {
			content: '';
			display: none;
			position: absolute;
			@include rem('top', 1.25px);
			@include rem('left', 4.5px);
			@include rem('width', 4px);
			@include rem('height', 8px);
			border: solid $white;
			@include rem('border-width', 0 3px 3px 0);
			transform: rotate(45deg);
		}
	}

	.control-label {
		@include rem('padding-left', 10px);
		color: $text-base-color;
	}

	input {
		/* Hide the browser's default checkbox but let it still be able to take focus for accessibility */
		position: absolute;
		z-index: -1;
		left: -10000px;
		width: 1px;
		height: 1px;
		opacity: 0;
		outline: none;

		&:focus {
			& ~ .custom-checkbox {
				border-color: $b360-color--black;
				box-shadow: 0px 0px 0px 1px $b360-color--black;
			}
		}

		/* When the checkbox is checked, add a blue background & display custom checkmark */
		&:checked {
			& ~ .custom-checkbox {
				background-color: $brand-blue;
				border-color: $brand-blue;
				&:after {
					display: block;
				}
			}
		}
		&:disabled {
			& ~ .custom-checkbox {
				background-color: $b360-color--gray__darker;
				cursor: not-allowed;
			}
		}
	}
}
</style>
